import React, { useState, useRef, useEffect } from 'react';
import { flag } from '../config';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LayoutDashboard, Users, Building2, MapPin, Settings, Trophy, Home, ChevronDown, Menu, X, Mail, CreditCard, Palette } from 'lucide-react';
import DashboardMobileDrawer from './dashboard/DashboardMobileDrawer';
import DashboardMobileNav from './dashboard/DashboardMobileNav';

const AdminNavigation = ({ admin }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
    setOpenDropdown(null);
    setMobileMenuOpen(false);
  };

  // Determine active page for nav highlighting
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isSettingsActive = location.pathname === '/admin/email-templates' || location.pathname === '/admin/subscriptions' || location.pathname === '/admin/coaster-designs';
  const isRewardsActive = location.pathname === '/admin/rewards';

  // Color scheme for each section
  const colors = {
    dashboard: { bg: '#f97316', shadow: 'rgba(249, 115, 22, 0.5)', glow: 'rgba(249, 115, 22, 0.3)' }, // consumer/orange
    customers: { bg: '#f97316', shadow: 'rgba(249, 115, 22, 0.5)', glow: 'rgba(249, 115, 22, 0.3)' }, // consumer/orange
    businesses: { bg: '#8b5cf6', shadow: 'rgba(139, 92, 246, 0.5)', glow: 'rgba(139, 92, 246, 0.3)' }, // business/purple
    locations: { bg: '#14b8a6', shadow: 'rgba(20, 184, 166, 0.5)', glow: 'rgba(20, 184, 166, 0.3)' }, // venue/teal
    settings: { bg: '#6366f1', shadow: 'rgba(99, 102, 241, 0.5)', glow: 'rgba(99, 102, 241, 0.3)' }, // admin/indigo
    rewards: { bg: '#f97316', shadow: 'rgba(249, 115, 22, 0.5)', glow: 'rgba(249, 115, 22, 0.3)' }, // consumer/orange
  };
  
  // Helper function to get nav link styles
  const getNavLinkStyle = (active, colorKey) => {
    const color = colors[colorKey] || colors.dashboard;
    return active ? {
      backgroundColor: color.bg,
      boxShadow: `0 4px 15px ${color.shadow}, 0 0 30px ${color.glow}`
    } : {};
  };

  return (
    <>
      <header className="bg-[#1e1b4b] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            
            {/* Logo + Admin Badge + User */}
            <div className="flex items-center gap-3 md:gap-4">
              <img 
                src="/tappd-logo-white.png" 
                alt="Tappd" 
                className="h-7 md:h-8 w-auto" 
              />
              <span className="px-2.5 md:px-3 py-1 bg-red-500 text-white text-[10px] md:text-xs font-bold rounded-md shadow-[0_2px_10px_rgba(239,68,68,0.5),0_0_20px_rgba(239,68,68,0.3)]">
                ADMIN
              </span>
              <div className="h-5 w-px bg-white/20 hidden md:block"></div>
              {admin && (
                <span className="text-white/40 text-sm hidden md:inline">
                  {admin.full_name || admin.username || admin.first_name}
                </span>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all"
            >
              <Menu size={24} aria-hidden="true" />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1" ref={dropdownRef}>
              <button
                onClick={() => handleNavigate('/admin/dashboard')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  isActive('/admin/dashboard') ? 'text-white font-semibold' : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                }`}
                style={getNavLinkStyle(isActive('/admin/dashboard'), 'dashboard')}
              >
                Dashboard
              </button>
              
              <button
                onClick={() => handleNavigate('/admin/customers')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  isActive('/admin/customers') ? 'text-white font-semibold' : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                }`}
                style={getNavLinkStyle(isActive('/admin/customers'), 'customers')}
              >
                Users
              </button>
              
              <button
                onClick={() => handleNavigate('/admin/businesses')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  isActive('/admin/businesses') ? 'text-white font-semibold' : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                }`}
                style={getNavLinkStyle(isActive('/admin/businesses'), 'businesses')}
              >
                Businesses
              </button>
              
              <button
                onClick={() => handleNavigate('/admin/locations')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  isActive('/admin/locations') ? 'text-white font-semibold' : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                }`}
                style={getNavLinkStyle(isActive('/admin/locations'), 'locations')}
              >
                Locations
              </button>

              {/* Settings Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setOpenDropdown(openDropdown === 'settings' ? null : 'settings')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1 ${
                    isSettingsActive
                      ? 'text-white font-semibold'
                      : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                  }`}
                  style={isSettingsActive ? {
                    backgroundColor: colors.settings.bg,
                    boxShadow: `0 4px 15px ${colors.settings.shadow}, 0 0 30px ${colors.settings.glow}`
                  } : {}}
                >
                  Settings
                  <ChevronDown size={14} className={`transition-transform ${openDropdown === 'settings' ? 'rotate-180' : ''}`} />
                </button>

                {openDropdown === 'settings' && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-lg border border-navy/10 py-2 min-w-[180px] z-50">
                    <button
                      onClick={() => handleNavigate('/admin/subscriptions')}
                      className="w-full text-left px-4 py-2.5 hover:bg-cream text-navy/70 hover:text-navy font-medium text-sm flex items-center gap-2 transition-colors"
                    >
                      <CreditCard size={16} />
                      Subscriptions
                    </button>
                    <button
                      onClick={() => handleNavigate('/admin/email-templates')}
                      className="w-full text-left px-4 py-2.5 hover:bg-cream text-navy/70 hover:text-navy font-medium text-sm flex items-center gap-2 transition-colors"
                    >
                      <Mail size={16} />
                      Email Templates
                    </button>
                    <button
                      onClick={() => handleNavigate('/admin/coaster-designs')}
                      className="w-full text-left px-4 py-2.5 hover:bg-cream text-navy/70 hover:text-navy font-medium text-sm flex items-center gap-2 transition-colors"
                    >
                      <Palette size={16} />
                      Coaster Designs
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={() => handleNavigate('/admin/venue-applications')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  isActive('/admin/venue-applications') ? 'text-white font-semibold' : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                }`}
                style={getNavLinkStyle(isActive('/admin/venue-applications'), 'venues')}
              >
                Venue Applications
              </button>

              {flag('REWARDS') && (<>
              {/* Rewards */}
              <button
                onClick={() => handleNavigate('/admin/rewards')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  isRewardsActive ? 'text-white font-semibold' : 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                }`}
                style={getNavLinkStyle(isRewardsActive, 'rewards')}
              >
                Rewards
              </button>
              </>)}
            </nav>

            {/* Right Side - Log out + Homepage (Desktop) */}
            <button
              type="button"
              onClick={async () => { try { await logout(); } finally { window.location.href = '/'; } }}
              className="hidden md:flex text-white/50 hover:text-white text-sm transition-colors items-center gap-1 mr-4"
            >
              Log out
            </button>
            <Link
              to="/"
              className="hidden md:flex text-white/50 hover:text-white text-sm transition-colors items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
              Homepage
            </Link>
          </div>
        </div>
      </header>
      
      {/* Mobile Drawer */}
      <DashboardMobileDrawer
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        variant="admin"
      >
        <DashboardMobileNav
          variant="admin"
          user={admin}
          onClose={() => setMobileMenuOpen(false)}
          badges={{}}
        />
      </DashboardMobileDrawer>
    </>
  );
};

export default AdminNavigation;
